import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { WorkOrder } from '../../mocks/data/work-orders'
import { SUPPLY_PERSONAS, useSupplyPersonaStore, type SupplyPersona } from '../../store/role'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { useT } from '../../i18n'
import { SUPPLY_WORK_ORDERS_STRINGS } from '../../i18n/supply/work-orders'
import { workOrderExtraI18n } from '../../i18n/supply/work-order-extra'
import {
  DialogContent,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { WorkOrderDetail } from './WorkOrderDetail'
import {
  SupplyMobileWorkOrderFilters,
  type SupplyMobileWorkOrderFilterDraft,
} from './SupplyMobileWorkOrderFilters'
import { SupplyMobileWorkOrdersSurface } from './SupplyMobileWorkOrdersSurface'
import {
  SupplyDesktopWorkOrders,
  type SupplyDesktopWorkOrderFilters,
  type SupplyWorkOrderQueue,
} from './SupplyDesktopWorkOrders'
import { SupplyWorkOrdersRoleState } from './SupplyWorkOrdersRoleState'
import { formatOrderRef, formatWorkOrderRef } from './workOrderDisplay'

type Response = {
  data: WorkOrder[]
  meta: { page: number; per_page: number; total: number; total_pages: number }
}

const EMPTY_MOBILE_FILTERS: SupplyMobileWorkOrderFilterDraft = {
  fulfillmentMode: '',
  dateFrom: '',
  dateTo: '',
  overdueOnly: false,
}

const EMPTY_WORK_ORDERS: WorkOrder[] = []

const EMPTY_DESKTOP_FILTERS: SupplyDesktopWorkOrderFilters = {
  search: '',
  ...EMPTY_MOBILE_FILTERS,
}

function matchesQueue(
  workOrder: WorkOrder,
  queue: SupplyWorkOrderQueue,
): boolean {
  return (
    queue === 'all' ||
    (queue === 'assigned' &&
      ['assigned', 'acknowledged'].includes(workOrder.state)) ||
    (queue === 'active' && workOrder.state === 'in_production') ||
    (queue === 'dispatched' &&
      ['dispatched_to_warehouse', 'dispatched_to_customer'].includes(
        workOrder.state,
      )) ||
    (queue === 'completed' &&
      workOrder.state === 'received_at_warehouse')
  )
}

function isOverdue(workOrder: WorkOrder, now: number): boolean {
  const terminal = [
    'received_at_warehouse',
    'dispatched_to_customer',
    'cancelled',
  ].includes(workOrder.state)
  return !terminal && new Date(workOrder.due_date).getTime() < now
}

function filterWorkOrders(
  workOrders: WorkOrder[],
  queue: SupplyWorkOrderQueue,
  search: string,
  filters: SupplyMobileWorkOrderFilterDraft,
  now: number,
): WorkOrder[] {
  const normalizedSearch = search.trim().toLocaleLowerCase()

  return workOrders.filter((workOrder) => {
    if (!matchesQueue(workOrder, queue)) return false

    if (normalizedSearch) {
      const searchValue = [
        workOrder.id,
        formatWorkOrderRef(workOrder.id),
        workOrder.product_id,
        workOrder.product_name,
        workOrder.customer_order_ref,
        formatOrderRef(workOrder.customer_order_ref),
      ]
        .join(' ')
        .toLocaleLowerCase()
      if (!searchValue.includes(normalizedSearch)) return false
    }

    if (
      filters.fulfillmentMode &&
      workOrder.fulfillment_mode !== filters.fulfillmentMode
    ) {
      return false
    }

    const dueDate = workOrder.due_date.slice(0, 10)
    if (filters.dateFrom && dueDate < filters.dateFrom) return false
    if (filters.dateTo && dueDate > filters.dateTo) return false
    if (filters.overdueOnly && !isOverdue(workOrder, now)) return false

    return true
  })
}

export function MyWorkOrders() {
  const navigate = useNavigate()
  const persona = useSupplyPersonaStore((s) => s.supplyPersona)
  const setSupplyPersona = useSupplyPersonaStore((s) => s.setSupplyPersona)
  const info = SUPPLY_PERSONAS[persona]
  const t = useT(SUPPLY_WORK_ORDERS_STRINGS)
  const tWo = useT(SUPPLY_WORK_ORDERS_STRINGS).workOrderDetail
  const tx = useT(workOrderExtraI18n)

  // Real-engine: identity + SO2/SO3 layout derive from the logged-in JWT org,
  // not the maket persona switcher (a real vendor must not be gated behind, or
  // shown, the demo personas). Maket default unchanged (query disabled).
  const realMe = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 5 * 60 * 1000,
  })
  const effectiveOrgType = USE_REAL_API
    ? (realMe.data?.active_membership?.org_type ?? 'SO2')
    : info.org_type
  const displayName = USE_REAL_API
    ? (realMe.data?.active_membership?.persona_label ??
        realMe.data?.user.display_name ??
        info.org_name)
    : info.org_name

  // Smoke-test #20-25 (Volkan 2026-06-03): row click opens detail in popup
  // modal — cross-shell consistency via
  // [[backlog_style_guideline_shell_detail_modal]].
  const [selectedWoId, setSelectedWoId] = useState<string | null>(null)
  const [mobileQueue, setMobileQueue] = useState<SupplyWorkOrderQueue>('all')
  const [mobileSearch, setMobileSearch] = useState('')
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [mobileFilters, setMobileFilters] =
    useState<SupplyMobileWorkOrderFilterDraft>(EMPTY_MOBILE_FILTERS)
  const [mobileFilterDraft, setMobileFilterDraft] =
    useState<SupplyMobileWorkOrderFilterDraft>(EMPTY_MOBILE_FILTERS)
  const [desktopQueue, setDesktopQueue] =
    useState<SupplyWorkOrderQueue>('all')
  const [desktopFilters, setDesktopFilters] =
    useState<SupplyDesktopWorkOrderFilters>(EMPTY_DESKTOP_FILTERS)
  const [renderNow] = useState(() => Date.now())

  const { data, isLoading, isError, isFetching, refetch } = useQuery<Response>({
    queryKey: ['supply', 'work-orders', persona],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/supply/my-work-orders?persona=${persona}`))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const mobileQueues = [
    { value: 'all', label: t.myWorkOrders.mobile.queues.all },
    { value: 'assigned', label: t.myWorkOrders.mobile.queues.assigned },
    { value: 'active', label: t.myWorkOrders.mobile.queues.active },
    {
      value: 'dispatched',
      label: t.myWorkOrders.mobile.queues.dispatched,
    },
    { value: 'completed', label: t.myWorkOrders.mobile.queues.completed },
  ] satisfies ReadonlyArray<{
    value: SupplyWorkOrderQueue
    label: string
  }>

  const allWorkOrders = data?.data ?? EMPTY_WORK_ORDERS
  const mobileWorkOrders = useMemo(
    () =>
      filterWorkOrders(
        allWorkOrders,
        mobileQueue,
        mobileSearch,
        mobileFilters,
        renderNow,
      ),
    [allWorkOrders, mobileFilters, mobileQueue, mobileSearch, renderNow],
  )
  const desktopWorkOrders = useMemo(
    () =>
      filterWorkOrders(
        allWorkOrders,
        desktopQueue,
        desktopFilters.search,
        desktopFilters,
        renderNow,
      ),
    [allWorkOrders, desktopFilters, desktopQueue, renderNow],
  )
  const overdueCount = useMemo(
    () => allWorkOrders.filter((workOrder) => isOverdue(workOrder, renderNow)).length,
    [allWorkOrders, renderNow],
  )

  const activeMobileFilterCount = [
    mobileFilters.fulfillmentMode,
    mobileFilters.dateFrom || mobileFilters.dateTo,
    mobileFilters.overdueOnly,
  ].filter(Boolean).length

  const openMobileFilters = () => {
    setMobileFilterDraft(mobileFilters)
    setMobileFiltersOpen(true)
  }

  const applyMobileFilters = () => {
    setMobileFilters(mobileFilterDraft)
    setMobileFiltersOpen(false)
  }

  if (effectiveOrgType !== 'SO2') {
    const so2Personas = Object.values(SUPPLY_PERSONAS).filter(
      (p) => p.org_type === 'SO2',
    )
    return (
      <SupplyWorkOrdersRoleState
        currentPersona={`${displayName} (${effectiveOrgType})`}
        simulation={!USE_REAL_API}
        options={so2Personas.map((candidate) => ({
          value: candidate.id,
          label: candidate.org_name,
        }))}
        onSwitch={(nextPersona) =>
          setSupplyPersona(nextPersona as SupplyPersona)
        }
        onBack={() => void navigate({ to: '/supply' })}
      />
    )
  }

  return (
    <div className="w-full">
      <SupplyMobileWorkOrdersSurface
        queue={mobileQueue}
        queues={mobileQueues}
        total={mobileWorkOrders.length}
        search={mobileSearch}
        searchOpen={mobileSearchOpen}
        activeFilterCount={activeMobileFilterCount}
        filterOpen={mobileFiltersOpen}
        refreshing={isFetching && !isLoading}
        workOrders={mobileWorkOrders}
        loading={isLoading}
        error={isError}
        now={renderNow}
        labels={{
          queue: t.myWorkOrders.mobile.queueLabel,
          count: t.myWorkOrders.mobile.count,
          search: t.myWorkOrders.mobile.search,
          closeSearch: t.myWorkOrders.mobile.closeSearch,
          searchPlaceholder: t.myWorkOrders.mobile.searchPlaceholder,
          filters: t.myWorkOrders.mobile.filters,
          filterTitle: t.myWorkOrders.mobile.filterTitle,
          cancel: t.myWorkOrders.mobile.cancel,
          apply: t.myWorkOrders.mobile.apply,
          clear: t.myWorkOrders.mobile.clear,
          refresh: t.myWorkOrders.actions.refresh,
        }}
        listLabels={{
          loading: t.myWorkOrders.states.loading,
          loadFailed: t.myWorkOrders.states.error,
          retry: t.myWorkOrders.mobile.retry,
          empty: t.myWorkOrders.table.emptyState.description,
          openWorkOrder: t.myWorkOrders.mobile.openWorkOrder,
          quantity: t.myWorkOrders.mobile.quantity,
          due: t.myWorkOrders.mobile.due,
          overdue: t.myWorkOrders.mobile.overdue,
          dueToday: t.myWorkOrders.mobile.dueToday,
          route: {
            hub_via_nj: t.myWorkOrders.mobile.route.hubViaNj,
            direct_dropship:
              t.myWorkOrders.mobile.route.directDropship,
          },
          status: {
            assigned: tWo.statusLabel.assigned,
            acknowledged: tWo.statusLabel.acknowledged,
            in_production: tWo.statusLabel.inProduction,
            dispatched_to_warehouse:
              tWo.statusLabel.dispatchedToWarehouse,
            received_at_warehouse:
              tWo.statusLabel.receivedAtWarehouse,
            dispatched_to_customer:
              tWo.statusLabel.dispatchedToCustomer,
            cancelled: tWo.statusLabel.cancelled,
          },
        }}
        filterContent={
          <SupplyMobileWorkOrderFilters
            value={mobileFilterDraft}
            labels={{
              route: t.myWorkOrders.mobile.routeLabel,
              allRoutes: t.myWorkOrders.mobile.allRoutes,
              routeValues: {
                hub_via_nj: t.myWorkOrders.mobile.route.hubViaNj,
                direct_dropship:
                  t.myWorkOrders.mobile.route.directDropship,
              },
              dueDate: t.myWorkOrders.mobile.dueDate,
              overdueOnly: t.myWorkOrders.mobile.overdueOnly,
            }}
            onChange={setMobileFilterDraft}
          />
        }
        onQueueChange={setMobileQueue}
        onSearchToggle={() =>
          setMobileSearchOpen((current) => !current)
        }
        onSearchChange={setMobileSearch}
        onFilterOpen={openMobileFilters}
        onFilterCancel={() => setMobileFiltersOpen(false)}
        onFilterApply={applyMobileFilters}
        onFilterClear={() =>
          setMobileFilterDraft(EMPTY_MOBILE_FILTERS)
        }
        onRefresh={() => void refetch()}
        onOpenWorkOrder={setSelectedWoId}
        onRetry={() => void refetch()}
      />

      <SupplyDesktopWorkOrders
        displayName={displayName}
        workOrders={desktopWorkOrders}
        total={allWorkOrders.length}
        overdueCount={overdueCount}
        queue={desktopQueue}
        filters={desktopFilters}
        loading={isLoading}
        error={isError}
        refreshing={isFetching && !isLoading}
        now={renderNow}
        onQueueChange={setDesktopQueue}
        onFiltersChange={setDesktopFilters}
        onReset={() => {
          setDesktopQueue('all')
          setDesktopFilters(EMPTY_DESKTOP_FILTERS)
        }}
        onRefresh={() => void refetch()}
        onRetry={() => void refetch()}
        onOpenWorkOrder={setSelectedWoId}
      />

      {/* Smoke-test #20-25 (Volkan 2026-06-03) — popup modal for row click,
          mirroring admin/seller pattern. See
          [[backlog_style_guideline_shell_detail_modal]]. */}
      <Dialog
        open={selectedWoId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedWoId(null)
        }}
      >
        <DialogContent
          className="w-[97vw] sm:max-w-[1200px] max-h-[94vh] overflow-y-auto p-0"
        >
          <DialogTitle className="sr-only">{tx.workOrderDetail}</DialogTitle>
          {selectedWoId && (
            <div className="p-6">
              <WorkOrderDetail id={selectedWoId} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
